import { captureException } from "@sentry/react";
import { useEffect, useState } from "react";
import {
  isRouteErrorResponse,
  useParams,
  useRouteError,
} from "react-router-dom";

import { useDynamicImportErrorHandler } from "@oneleet/utils";

import { FullPageError } from "@oneleet/components";

let globalErrorId: string | undefined;

export function ErrorBoundary({
  showLogoutButton,
}: {
  showLogoutButton?: boolean;
}) {
  const { tenant: tenantId } = useParams();
  const error = useRouteError();

  // Handle dynamic import errors by reloading the page when stale bundles are detected
  useDynamicImportErrorHandler(error);

  let errorStatus;
  let errorText;
  let errorDescription;

  const [sentryErrorId, setSentryErrorId] = useState<string | undefined>(
    globalErrorId,
  );

  useEffect(() => {
    if (globalErrorId) return;
    const errorId = captureException(error, {
      level: "fatal",
      tags: {
        ...(tenantId ? { tenantId } : {}),
      },
    });
    setSentryErrorId(errorId);
    globalErrorId = errorId;
  }, [error, tenantId]);

  if (isRouteErrorResponse(error)) {
    if (error.status === 403) {
      errorStatus = "403";
      errorText = "Not allowed";
      errorDescription =
        "You are not allowed to access this page. Please contact your team administrator.";
    }

    if (error.status === 404) {
      errorStatus = "404";
      errorText = "Not found";
      errorDescription = "Please double check the URL and try again.";
    }
  }

  return (
    <FullPageError
      status={errorStatus}
      title={errorText}
      description={errorDescription}
      showLogoutButton={showLogoutButton}
      sentryErrorId={sentryErrorId}
    />
  );
}
